import React from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Laptop, Tablet, Gamepad2, Watch, Droplets, ArrowRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import FadeInOnScroll from './FadeInOnScroll';

// `slug: null` services don't have their own landing page yet.
const services = [
  {
    icon: Smartphone,
    title: 'Phone Repair',
    description: 'Screen replacements, battery, charging ports, and more',
    slug: 'iphone-repair',
  },
  {
    icon: Laptop,
    title: 'Computer Repair',
    description: 'Hardware fixes, upgrades, virus removal, and optimization',
    slug: 'laptop-repair',
  },
  {
    icon: Tablet,
    title: 'Tablet Repair',
    description: 'Screen and digitizer repairs for all tablet brands',
    slug: 'tablet-repair',
  },
  {
    icon: Gamepad2,
    title: 'Gaming Console Repair',
    description: 'Xbox, PlayStation, Nintendo Switch repairs',
    slug: 'gaming-console-repair',
  },
  {
    icon: Watch,
    title: 'Smartwatch Repair',
    description: 'Apple Watch, Galaxy Watch, and other smartwatch repairs',
    slug: null,
  },
  {
    icon: Droplets,
    title: 'Water Damage Repair',
    description: 'Professional liquid damage restoration services',
    slug: 'water-damage-repair',
  },
];

const ServicesSection = () => {
  return (
    <section
      id="services"
      className="py-20 px-6 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-[0.07]"
        style={{ background: 'radial-gradient(circle, #00B3B3 0%, transparent 70%)' }}
      />

      <div className="max-w-7xl mx-auto relative">
        <FadeInOnScroll className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
          <div className="w-24 h-1 mx-auto" style={{ backgroundColor: '#00B3B3' }} />
        </FadeInOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const CardBody = (
              <Card className="glass-card border-0 group h-full">
                <CardContent className="p-8 text-center">
                  <div
                    className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300"
                    style={{
                      background: 'linear-gradient(135deg, #00B3B3 0%, #14e3e3 100%)',
                      boxShadow: '0 10px 30px -10px rgba(0, 179, 179, 0.6)',
                    }}
                  >
                    <service.icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                  {service.slug && (
                    <span
                      className="inline-flex items-center gap-1 text-sm font-semibold group-hover:gap-2 transition-all"
                      style={{ color: '#00B3B3' }}
                    >
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </CardContent>
              </Card>
            );

            return (
              <FadeInOnScroll key={index} delay={index * 90}>
                {service.slug ? (
                  <Link
                    to={`/${service.slug}`}
                    className="block h-full"
                    data-testid={`service-card-${service.slug}`}
                  >
                    {CardBody}
                  </Link>
                ) : (
                  CardBody
                )}
              </FadeInOnScroll>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
